import { useCallback, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import "./ManagerCheck.css";
import ProjectCard from "../../components/ProjectCard/ProjectCard";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ErrorState from "../../components/ErrorState/ErrorState";
import PopupComponent from "../../components/PopupComponent/PopupComponent";
import HeaderComponent from "../../components/HeaderComponent/HeaderComponent";
import SortSelect, { SortKey } from "../../components/SortSelect/SortSelect";

import { useDebounce } from "../../customHooks/useDebounce";
import { useProjectChecks } from "../../customHooks/useProjectChecks";
import { dynamicSort } from "../../utils/dynamicSort";
import { filterBySearch } from "../../utils/filterBySearch";
import { useTimePeriodContext } from "../../contexts/TimePeriodContext";

function ManagerCheck() {
  const {
    projectChecks,
    hasChanges,
    fetchProjectChecks,
    saveProjectChecks,
    updateCheck,
    confirmAll,
    loading,
    fetchError,
    saveError,
    allChecked,
    hasCompletedCheck,
  } = useProjectChecks();

  const { hasPendingChange, confirmPending, cancelPending } = useTimePeriodContext();

  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("clientName");
  const [thenBy, setThenBy] = useState<SortKey | null>(null);

  const debouncedSearch = useDebounce(searchInput, 300);

  const filteredProjectChecks = useMemo(() => {
    let result = [...projectChecks];

    result = dynamicSort(result, sortBy, thenBy);

    if (!debouncedSearch.trim()) return result;

    const term = debouncedSearch.toLowerCase();

    return filterBySearch(term, result);
  }, [projectChecks, debouncedSearch, sortBy, thenBy]);

  async function checkIfCompleted() {
    if (
      !projectChecks.some(
        (check) => check.checkedByManager === false && check.managerComment.trim() === "",
      )
    )
      setShowPopup(true);
    else await saveProjectChecks(false);
  }

  const handleClose = useCallback(() => {
    setShowPopup(false);
  }, []);

  async function handleComplete() {
    await saveProjectChecks(true);
    if (!saveError) navigate("/manager-completed");
  }

  if (fetchError && !loading) {
    return (
      <div className="manager-page">
        <ErrorState type="fetch" onAction={fetchProjectChecks} fullPage />
      </div>
    );
  }

  return (
    <>
      <title>Projektkontrolle - Projektleiter</title>
      <div className="manager-page">
        {hasPendingChange && (
          <PopupComponent
            title="Ungespeicherte Änderungen"
            message="Wenn du den Zeitraum wechselst, gehen deine Änderungen verloren."
            confirmAction={confirmPending}
            confirmText="Verwerfen und wechseln"
            closeAction={cancelPending}
            closeText="Abbrechen"
          />
        )}
        {showPopup && (
          <PopupComponent
            title="Alle Projekte kontrolliert"
            message="Alle Projekte sind geprüft, aber du hast die Projektkontrolle noch nicht abgeschlossen. Möchtest du jetzt abschliessen?"
            confirmAction={handleComplete}
            confirmText="Ja, ich bin fertig."
            closeAction={handleClose}
            closeText="Nein, ich bin noch nicht fertig."
          />
        )}
        <HeaderComponent
          pageTitle={`Projektkontrolle durch Projektleiter`}
          backDestination="/"
          hasSearchBar
          hasTimePeriodPicker
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          entityToSearch="Projekte"
        />
        {hasCompletedCheck && (
          <div className="completed-banner">
            <span className="completed-banner-icon">✓</span>
            Diese Projektkontrolle wurde bereits abgeschlossen.
          </div>
        )}

        <div className="content-base manager-content">
          <div className="project-checks-list">
            <SortSelect
              sortBy={sortBy}
              setSortBy={setSortBy}
              thenBy={thenBy}
              setThenBy={setThenBy}
            />
            <LoadingSpinner loading={loading} isFullscreen={false} />
            {!fetchError &&
              !loading &&
              filteredProjectChecks.map((c) => (
                <ProjectCard
                  variant="manager"
                  key={c.id}
                  projectCheck={c}
                  onManagerChange={(id, value, type) => updateCheck(id, value, type)}
                />
              ))}
            {!loading && !fetchError && filteredProjectChecks.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <ErrorState type="empty" />
              </div>
            )}
          </div>
        </div>

        {!loading && !fetchError && projectChecks.length !== 0 && (
          <div className="footer">
            <button className="confirm-all" onClick={confirmAll}>
              Alle Projekte bestätigen
            </button>

            {saveError && <ErrorState type="save" />}

            <div className="footer-right">
              <button className="save" onClick={checkIfCompleted} disabled={!hasChanges}>
                Speichern
              </button>
              <button className="save-close" onClick={handleComplete} disabled={!allChecked}>
                Speichern & Abschliessen
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ManagerCheck;
